
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import isEqual from 'lodash/isEqual';
import { _ as _objectSpread } from './_rollupPluginBabelHelpers-mixin.js';
import { s as setStyles, a as setAttributes } from './table-auto-height-mixin.js';
import Popper from 'popper.js';
import uuid from 'uuid';
import { ResizeObserver } from 'vue-resize';
import clamp from 'lodash/clamp';

var script = {
  name: 'PickerPopper',
  components: {
    ResizeObserver
  },
  props: {
    fullWidth: {
      type: [Boolean, Number],
      default: false
    },
    isOpen: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    appendToBody: {
      type: Boolean
    },
    popperOptions: {
      type: Object
    },
    minWidth: {
      type: [String, Number]
    },
    maxWidth: {
      type: [String, Number]
    },
    maxHeight: {
      type: [String, Number]
    }
  },

  data() {
    return {
      isPopperOpening: false,
      isPopperDisposed: false,
      isPopperMounted: false,
      isPopperClosing: false,
      isWrapperMounted: false,
      id: uuid()
    };
  },

  computed: {
    contentStyles() {
      const styles = {};

      if (this.maxHeight) {
        if (typeof this.maxHeight === 'string') {
          styles.maxHeight = this.maxHeight;
        } else {
          styles.maxHeight = `${this.maxHeight}px`;
        }
      }

      if (!this.fullWidth && this.maxWidth) {
        if (typeof this.maxWidth === 'string') {
          styles.maxWidth = this.maxWidth;
        } else {
          styles.maxWidth = `${this.maxWidth}px`;
        }
      }

      return styles;
    },

    popperTarget() {
      if (this.appendToBody) {
        return window.document.body;
      }

      return this.$refs.reference.parentNode;
    },

    direction() {
      const placement = this.popperOptions.placement;

      if (placement) {
        return placement.split('-')[0];
      }

      return 'bottom';
    },

    referenceStyle() {
      if (this.fullWidth) {
        return {
          display: 'block'
        };
      }

      return {
        display: 'inline-block'
      };
    }

  },
  watch: {
    isOpen(val) {
      if (val) {
        this.showPopper();
      } else {
        window.removeEventListener('mousedown', this.handleClickOutside, true);
        this.hidePopper();
        this.isPopperClosing = true;
        requestAnimationFrame(() => {
          this.isPopperClosing = false;
        });
      }
    },

    disabled() {
      if (this.disabled && this.isOpen) {
        this.hidePopper();
      }
    },

    popperOptions: {
      handler(oldValue, newValue) {
        if (isEqual(oldValue, newValue)) {
          return;
        }

        this.restartPopper();
      },

      deep: true
    }
  },

  mounted() {
    const popper = this.$refs.popper;
    popper.parentNode && popper.parentNode.removeChild(popper);

    if (this.isOpen) {
      this.showPopper();
    }
  },

  beforeDestroy() {
    window.removeEventListener('mousedown', this.handleClickOutside, true);
    this.destroyPopper();
  },

  methods: {
    createPopper() {
      const _this$$refs = this.$refs,
            reference = _this$$refs.reference,
            popper = _this$$refs.popper;

      if (this.popperInstance) {
        this.popperInstance.enableEventListeners();
        this.scheduleUpdate();
      }

      if (!this.isPopperMounted) {
        this.popperTarget.appendChild(popper);
        this.isPopperMounted = true;
      }

      if (!this.popperInstance) {
        const popperOptions = _objectSpread({
          onUpdate: this.handleUpdate
        }, this.popperOptions, {
          placement: this.popperOptions.placement || 'bottom-start',
          modifiers: _objectSpread({}, this.popperOptions.modifiers, {
            offset: _objectSpread({}, this.popperOptions.modifiers && this.popperOptions.modifiers.offset, {
              offset: this.popperOptions.offset || '0, 8'
            }),
            preventOverflow: _objectSpread({
              escapeWithReference: true
            }, this.popperOptions.modifiers && this.popperOptions.modifiers.preventOverflow),
            flip: _objectSpread({
              enabled: true,
              flipVariationsByContent: true,
              behavior: ['bottom', 'top', 'bottom']
            }, this.popperOptions.modifiers && (this.popperOptions.modifiers.flip || this.popperOptions.modifiers.flipVariations)),
            applyStyle: _objectSpread({
              enabled: true,
              order: 900,
              fn: this.applyPopperStyle
            }, this.popperOptions.modifiers && this.popperOptions.modifiers.applyStyle)
          })
        });

        this.popperInstance = new Popper(reference, popper, popperOptions);
        requestAnimationFrame(() => {
          if (!this.isPopperDisposed && this.popperInstance) {
            this.scheduleUpdate();
          } else {
            this.destroyPopper();
          }
        });
      }
    },

    showPopper() {
      this.createPopper();
      window.addEventListener('mousedown', this.handleClickOutside, true);
      this.isPopperOpening = true;
      requestAnimationFrame(() => {
        this.isPopperOpening = false;
        this.scheduleUpdate();
      });
    },

    hidePopper() {
      if (!this.isOpen) {
        return;
      }

      if (this.popperInstance) {
        this.popperInstance.disableEventListeners();
      }
    },

    destroyPopper() {
      this.isPopperDisposed = true;
      this.hidePopper();

      if (this.popperInstance) {
        this.popperInstance.destroy();

        if (!this.popperInstance.options.removeOnDestroy) {
          const popper = this.$refs.popper;
          popper.parentNode && popper.parentNode.removeChild(popper);
        }
      }

      this.isPopperMounted = false;
      this.popperInstance = null;
      this.$emit('dispose');
    },

    restartPopper() {
      if (this.popperInstance) {
        this.destroyPopper();
        this.isPopperDisposed = false;

        if (this.isOpen) {
          this.showPopper();
        }
      }
    },

    handleClickOutside(event) {
      if (!this.isOpen || this.isPopperOpening) {
        return;
      }

      const _this$$refs2 = this.$refs,
            popper = _this$$refs2.popper,
            reference = _this$$refs2.reference;

      if (!popper || !reference) {
        return;
      }

      if (!popper.contains(event.target) && !reference.contains(event.target)) {
        this.$emit('click-outside', event);
      }
    },

    scheduleUpdate() {
      if (this.popperInstance) {
        this.popperInstance.scheduleUpdate();
      }
    },

    applyPopperStyle(data) {
      data.styles.minWidth = Math.max(this.minWidth, data.offsets.reference.width);

      if (this.fullWidth) {
        const width = clamp(this.minWidth, data.offsets.reference.width, this.maxWidth);
        data.styles.width = width;
      }

      if (!this.isPopperClosing) {
        setStyles(data.instance.popper, data.styles);
      }

      setAttributes(data.instance.popper, data.attributes);
      return data;
    },

    handleResize() {
      this.$emit('resize', this.popperInstance);

      if (this.isOpen) {
        this.scheduleUpdate();
      }
    },

    handleUpdate(data) {
      if (data.hide) {
        this.$emit('hide');
        this.popperInstance.disableEventListeners();
      }
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', [_c('span', {
    ref: "reference",
    style: _vm.referenceStyle,
    attrs: {
      "aria-describedby": _vm.id
    }
  }, [_vm._t("default")], 2), _vm._v(" "), _c('div', {
    ref: "popper",
    staticClass: "pendo-picker-popper__content-wrapper",
    class: _vm.popperOptions.class,
    attrs: {
      "id": _vm.id,
      "aria-hidden": _vm.isOpen ? 'false' : 'true'
    }
  }, [_c('transition', {
    attrs: {
      "name": "pendo-transition-slide-fade",
      "appear": ""
    }
  }, [_vm.isOpen ? _c('div', {
    class: ['pendo-picker-popper__content', "pendo-transition-slide-fade--direction-" + _vm.direction],
    style: _vm.contentStyles
  }, [_vm.isPopperMounted ? _vm._t("popper") : _vm._e(), _vm._v(" "), _c('ResizeObserver', {
    on: {
      "notify": _vm.handleResize
    }
  })], 2) : _vm._e()])], 1)]);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "picker-popover.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PickerPopover = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export { PickerPopover as P };
